/**
 * A small Markdown parser for assistant replies, producing a plain syntax tree
 * that `MarkdownView.tsx` turns into Preact elements.
 *
 * Why not a library: the widget ships on customers' pages under a 30 kB gzipped
 * budget (scripts/check-size.mjs), and react-markdown alone is larger than
 * that. This covers what models actually emit — headings, bold/italic/strike,
 * inline + fenced code, links, nested lists, blockquotes, rules and GFM
 * tables — and nothing else.
 *
 * Safety: the output is a tree of plain strings, never HTML, so nothing a model
 * (or a poisoned source document it echoes) writes can become markup. Raw HTML
 * in the input shows as literal text. Link targets are allow-listed in
 * `safeHref` — anything else (`javascript:`, `data:`, ...) renders as plain
 * text with no link.
 *
 * A single newline is a line break, not a space — same as the dashboard's
 * renderer (`remark-breaks` there), so a reply looks alike in both.
 */

export type Inline =
  | string
  | { t: 'strong' | 'em' | 'del'; c: Inline[] }
  | { t: 'code'; v: string }
  | { t: 'link'; href: string | null; c: Inline[] }
  | { t: 'br' };

export type Align = 'left' | 'center' | 'right' | null;

export type Block =
  | { t: 'p'; c: Inline[] }
  | { t: 'h'; level: number; c: Inline[] }
  | { t: 'pre'; v: string }
  | { t: 'hr' }
  | { t: 'quote'; c: Block[] }
  | { t: 'list'; ordered: boolean; start: number; items: Block[][] }
  | { t: 'table'; align: Align[]; head: Inline[][]; rows: Inline[][][] };

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/;
const HR = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}> ?(.*)$/;
const LIST = /^( *)([-*+]|\d{1,9}[.)])( +|$)(.*)$/;
const TABLE_DELIM = /^ *\|? *:?-+:? *(?:\| *:?-+:? *)*\|? *$/;
const SAFE_URL = /^(?:https?:|mailto:|tel:|\/|#)/i;
const WORD = /[\p{L}\p{N}]/u;
const ESCAPABLE = /[!-/:-@[-`{-~]/;
const AUTOLINK = /https?:\/\/[^\s<]+/y;

export function parseMarkdown(src: string): Block[] {
  return parseBlocks(src.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n'));
}

function indentOf(line: string): number {
  return line.length - line.trimStart().length;
}

function isTableStart(line: string, next: string | undefined): boolean {
  return (
    next !== undefined &&
    line.includes('|') &&
    next.includes('|') &&
    TABLE_DELIM.test(next) &&
    splitRow(line).length === splitRow(next).length
  );
}

function startsBlock(line: string, next: string | undefined): boolean {
  return (
    FENCE.test(line) ||
    HEADING.test(line) ||
    HR.test(line) ||
    QUOTE.test(line) ||
    LIST.test(line) ||
    isTableStart(line, next)
  );
}

function parseBlocks(lines: string[]): Block[] {
  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    let m: RegExpExecArray | null;

    if (!line.trim()) {
      i++;
    } else if ((m = FENCE.exec(line))) {
      const fence = m[1]!;
      const body: string[] = [];
      i++;
      // A fence still open mid-stream simply runs to the end of the text.
      while (i < lines.length && !lines[i]!.trimStart().startsWith(fence)) body.push(lines[i++]!);
      i++;
      out.push({ t: 'pre', v: body.join('\n') });
    } else if ((m = HEADING.exec(line))) {
      out.push({ t: 'h', level: m[1]!.length, c: parseInline(m[2] ?? '') });
      i++;
    } else if (HR.test(line)) {
      out.push({ t: 'hr' });
      i++;
    } else if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && (m = QUOTE.exec(lines[i]!))) {
        body.push(m[1]!);
        i++;
      }
      out.push({ t: 'quote', c: parseBlocks(body) });
    } else if (LIST.test(line)) {
      const [list, next] = parseList(lines, i);
      out.push(list);
      i = next;
    } else if (isTableStart(line, lines[i + 1])) {
      const [table, next] = parseTable(lines, i);
      out.push(table);
      i = next;
    } else {
      const body = [line.trim()];
      i++;
      while (i < lines.length && lines[i]!.trim() && !startsBlock(lines[i]!, lines[i + 1])) {
        body.push(lines[i++]!.trim());
      }
      out.push({ t: 'p', c: parseInline(body.join('\n')) });
    }
  }

  return out;
}

/**
 * A list runs while items share its marker kind (bullet vs number) at the same
 * or deeper indent than the first one. A line indented to an item's content
 * column belongs to that item — which is how `    *   **Name:** …` under
 * `*   **Best Value:**` becomes a nested list.
 */
function parseList(lines: string[], start: number): [Block, number] {
  const first = LIST.exec(lines[start]!)!;
  const indent = first[1]!.length;
  const ordered = /\d/.test(first[2]!);
  const items: Block[][] = [];
  let i = start;

  while (i < lines.length) {
    const m = LIST.exec(lines[i]!);
    if (!m || m[1]!.length < indent || /\d/.test(m[2]!) !== ordered) break;

    const contentCol = m[1]!.length + m[2]!.length + (Math.min(m[3]!.length, 4) || 1);
    const body = [m[4]!];
    let prevBlank = false;
    i++;

    while (i < lines.length) {
      const l = lines[i]!;
      if (!l.trim()) {
        body.push('');
        prevBlank = true;
      } else if (indentOf(l) >= contentCol) {
        body.push(l.slice(contentCol));
        prevBlank = false;
      } else if (!prevBlank && !startsBlock(l, lines[i + 1])) {
        // Lazy continuation: an unindented line that just carries on the text.
        body.push(l.trim());
      } else {
        break;
      }
      i++;
    }

    items.push(parseBlocks(body));
  }

  return [{ t: 'list', ordered, start: ordered ? parseInt(first[2]!, 10) : 1, items }, i];
}

function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);

  const cells: string[] = [];
  let cur = '';
  for (let k = 0; k < s.length; k++) {
    if (s[k] === '\\' && s[k + 1] === '|') {
      cur += '|';
      k++;
    } else if (s[k] === '|') {
      cells.push(cur.trim());
      cur = '';
    } else {
      cur += s[k];
    }
  }
  cells.push(cur.trim());
  return cells;
}

function parseTable(lines: string[], start: number): [Block, number] {
  const head = splitRow(lines[start]!);
  const align = splitRow(lines[start + 1]!).map((d): Align => {
    const l = d.startsWith(':');
    const r = d.endsWith(':');
    return l && r ? 'center' : r ? 'right' : l ? 'left' : null;
  });

  const rows: Inline[][][] = [];
  let i = start + 2;
  while (i < lines.length && lines[i]!.trim() && lines[i]!.includes('|')) {
    const cells = splitRow(lines[i++]!);
    // Rows are padded/truncated to the header width, as GFM does.
    rows.push(head.map((_, k) => parseInline(cells[k] ?? '')));
  }

  return [{ t: 'table', align, head: head.map((h) => parseInline(h)), rows }, i];
}

export function safeHref(url: string): string | null {
  const u = url.trim().replace(/^<|>$/g, '');
  return SAFE_URL.test(u) ? u : null;
}

function isWord(ch: string | undefined): boolean {
  return ch !== undefined && WORD.test(ch);
}

/** Index of the closing delimiter for an emphasis run opened just before `from`, or -1. */
function findClose(s: string, from: number, delim: string): number {
  const underscore = delim[0] === '_';
  if (from >= s.length || /\s/.test(s[from]!)) return -1;

  for (let j = from; j < s.length; j++) {
    if (s[j] === '\\') {
      j++;
      continue;
    }
    if (delim.length === 1 && s.startsWith(delim + delim, j)) {
      j++;
      continue;
    }
    if (j > from && s.startsWith(delim, j) && !/\s/.test(s[j - 1]!)) {
      // `***x***`: close on the last `**` of the run so the inner `*` pairs up.
      if (delim.length === 2) while (s[j + 2] === delim[0]) j++;
      if (underscore && isWord(s[j + delim.length])) continue;
      return j;
    }
  }
  return -1;
}

/** Index of the `]` matching the `[` at `open`, or -1. */
function findBracket(s: string, open: number): number {
  let depth = 0;
  for (let j = open; j < s.length; j++) {
    if (s[j] === '\\') j++;
    else if (s[j] === '[') depth++;
    else if (s[j] === ']' && --depth === 0) return j;
  }
  return -1;
}

/** Index of the `)` closing a link target that starts at `from`, allowing balanced parens. */
function findParen(s: string, from: number): number {
  let depth = 1;
  for (let j = from; j < s.length; j++) {
    if (s[j] === '\n') return -1;
    if (s[j] === '(') depth++;
    else if (s[j] === ')' && --depth === 0) return j;
  }
  return -1;
}

function trimAutolink(url: string): string {
  let u = url.replace(/[.,;:!?'"*_~]+$/, '');
  while (u.endsWith(')') && u.split('(').length < u.split(')').length) u = u.slice(0, -1);
  return u;
}

export function parseInline(s: string): Inline[] {
  const out: Inline[] = [];
  let text = '';
  const flush = () => {
    if (text) out.push(text);
    text = '';
  };

  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;

    if (ch === '\\' && i + 1 < s.length && ESCAPABLE.test(s[i + 1]!)) {
      text += s[i + 1];
      i += 2;
      continue;
    }

    if (ch === '\n') {
      flush();
      out.push({ t: 'br' });
      i++;
      continue;
    }

    if (ch === '`') {
      let n = 1;
      while (s[i + n] === '`') n++;
      const end = s.indexOf('`'.repeat(n), i + n);
      if (end !== -1) {
        flush();
        out.push({ t: 'code', v: s.slice(i + n, end).trim() });
        i = end + n;
      } else {
        text += s.slice(i, i + n);
        i += n;
      }
      continue;
    }

    const delim = s.startsWith('**', i)
      ? '**'
      : s.startsWith('__', i)
        ? '__'
        : s.startsWith('~~', i)
          ? '~~'
          : ch === '*' || ch === '_'
            ? ch
            : '';
    if (delim) {
      const end = delim[0] === '_' && isWord(s[i - 1]) ? -1 : findClose(s, i + delim.length, delim);
      if (end !== -1) {
        flush();
        const t = delim === '~~' ? 'del' : delim.length === 2 ? 'strong' : 'em';
        out.push({ t, c: parseInline(s.slice(i + delim.length, end)) });
        i = end + delim.length;
      } else {
        text += delim;
        i += delim.length;
      }
      continue;
    }

    // Images render as links to their target — a chat reply never pulls an
    // image onto the customer's page.
    const bracket = ch === '[' ? i : ch === '!' && s[i + 1] === '[' ? i + 1 : -1;
    if (bracket !== -1) {
      const close = findBracket(s, bracket);
      const end = close !== -1 && s[close + 1] === '(' ? findParen(s, close + 2) : -1;
      if (end !== -1) {
        flush();
        const target =
          s
            .slice(close + 2, end)
            .trim()
            .split(/\s+/)[0] ?? '';
        out.push({
          t: 'link',
          href: safeHref(target),
          c: parseInline(s.slice(bracket + 1, close)),
        });
        i = end + 1;
        continue;
      }
    }

    if (ch === '<') {
      const end = s.indexOf('>', i);
      const inner = end !== -1 ? s.slice(i + 1, end) : '';
      if (/^https?:\/\/[^\s<>]+$/.test(inner)) {
        flush();
        out.push({ t: 'link', href: inner, c: [inner] });
        i = end + 1;
        continue;
      }
    }

    if (ch === 'h' && !isWord(s[i - 1])) {
      AUTOLINK.lastIndex = i;
      const m = AUTOLINK.exec(s);
      if (m) {
        const url = trimAutolink(m[0]);
        flush();
        out.push({ t: 'link', href: url, c: [url] });
        i += url.length;
        continue;
      }
    }

    text += ch;
    i++;
  }

  flush();
  return out;
}
